import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Receipt, RefreshCw, CreditCard } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SalesInvoices() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    supply_type: 'intra',
    items: [{ description: 'Internet Service', hsn_code: '998314', quantity: 1, unit_price: 0 }],
    notes: ''
  });
  const [payment, setPayment] = useState({ amount: 0, payment_date: new Date().toISOString().split('T')[0], payment_mode: 'bank_transfer', reference_number: '' });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, custRes] = await Promise.all([
        fetch(`${API}/api/sales/invoices`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/sales/customers`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (invRes.ok) setInvoices((await invRes.json()).invoices || []);
      if (custRes.ok) {
        const data = await custRes.json();
        setCustomers([...(data.customers || []), ...(data.sub_customers || []).map(s => ({ ...s, customer_id: s.sub_customer_id }))]);
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createInvoice = async () => {
    if (!newInvoice.customer_id || !newInvoice.items[0].unit_price) {
      toast.error('Please fill required fields');
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/sales/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newInvoice)
      });
      if (res.ok) {
        toast.success('Invoice created');
        setShowNew(false);
        setNewInvoice({ customer_id: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', supply_type: 'intra', items: [{ description: 'Internet Service', hsn_code: '998314', quantity: 1, unit_price: 0 }], notes: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create invoice');
      }
    } catch (error) { toast.error('Failed to create invoice'); }
    finally { setProcessing(false); }
  };

  const recordPayment = async () => {
    if (!selectedInvoice || payment.amount <= 0) {
      toast.error('Please enter valid amount');
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/sales/payments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: selectedInvoice.customer_id,
          invoice_ids: [selectedInvoice.invoice_id],
          ...payment
        })
      });
      if (res.ok) {
        toast.success('Payment recorded');
        setShowPay(false);
        setSelectedInvoice(null);
        setPayment({ amount: 0, payment_date: new Date().toISOString().split('T')[0], payment_mode: 'bank_transfer', reference_number: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to record payment');
      }
    } catch (error) { toast.error('Failed to record payment'); }
    finally { setProcessing(false); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" />Invoices</CardTitle>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New Invoice</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create Invoice</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Customer *</Label>
                  <Select value={newInvoice.customer_id} onValueChange={(v) => setNewInvoice({ ...newInvoice, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name || c.contact_person} {c.gst_number ? '(B2B)' : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Invoice Date</Label><Input type="date" value={newInvoice.invoice_date} onChange={(e) => setNewInvoice({ ...newInvoice, invoice_date: e.target.value })} /></div>
                  <div><Label>Due Date</Label><Input type="date" value={newInvoice.due_date} onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })} /></div>
                  <div><Label>Supply Type</Label>
                    <Select value={newInvoice.supply_type} onValueChange={(v) => setNewInvoice({ ...newInvoice, supply_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="intra">Intra-State</SelectItem>
                        <SelectItem value="inter">Inter-State</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="border rounded p-3 bg-slate-50">
                  <Label className="text-sm font-medium">Line Item</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><Label className="text-xs">Description</Label><Input value={newInvoice.items[0].description} onChange={(e) => setNewInvoice({ ...newInvoice, items: [{ ...newInvoice.items[0], description: e.target.value }] })} /></div>
                    <div><Label className="text-xs">HSN</Label><Input value={newInvoice.items[0].hsn_code} onChange={(e) => setNewInvoice({ ...newInvoice, items: [{ ...newInvoice.items[0], hsn_code: e.target.value }] })} /></div>
                    <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={newInvoice.items[0].quantity} onChange={(e) => setNewInvoice({ ...newInvoice, items: [{ ...newInvoice.items[0], quantity: parseInt(e.target.value) || 1 }] })} /></div>
                    <div><Label className="text-xs">Unit Price *</Label><Input type="number" min="0" value={newInvoice.items[0].unit_price} onChange={(e) => setNewInvoice({ ...newInvoice, items: [{ ...newInvoice.items[0], unit_price: parseFloat(e.target.value) || 0 }] })} /></div>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={newInvoice.notes} onChange={(e) => setNewInvoice({ ...newInvoice, notes: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                <Button onClick={createInvoice} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Create Invoice</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.invoice_id}>
                <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                <TableCell>{inv.invoice_date}</TableCell>
                <TableCell>{inv.customer_name}</TableCell>
                <TableCell><Badge variant={inv.invoice_type === 'B2B' ? 'default' : 'secondary'}>{inv.invoice_type}</Badge></TableCell>
                <TableCell className="text-right">{formatCurrency(inv.total_amount)}</TableCell>
                <TableCell className="text-right text-green-600">{formatCurrency(inv.paid_amount)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={inv.payment_status === 'paid' ? 'border-green-500 text-green-600' : inv.payment_status === 'partial' ? 'border-amber-500 text-amber-600' : 'border-red-500 text-red-600'}>
                    {inv.payment_status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button variant="outline" size="sm" disabled={inv.payment_status === 'paid'} onClick={() => { setSelectedInvoice(inv); setPayment({ ...payment, amount: inv.balance_due || (inv.total_amount - (inv.paid_amount || 0)) }); setShowPay(true); }}>
                    <CreditCard className="h-3 w-3 mr-1" />Pay
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {invoices.length === 0 && <div className="text-center py-8 text-slate-500">No invoices found</div>}
      </CardContent>

      {/* Payment Dialog */}
      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amount *</Label><Input type="number" min="0" value={payment.amount} onChange={(e) => setPayment({ ...payment, amount: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Date</Label><Input type="date" value={payment.payment_date} onChange={(e) => setPayment({ ...payment, payment_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Mode</Label>
                <Select value={payment.payment_mode} onValueChange={(v) => setPayment({ ...payment, payment_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Reference</Label><Input value={payment.reference_number} onChange={(e) => setPayment({ ...payment, reference_number: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(false)}>Cancel</Button>
            <Button onClick={recordPayment} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
